import React, { Suspense, lazy } from 'react';
import { useState, useEffect } from 'react';
import './App.css';

import { Outlet, Route, Routes, useSearchParams } from "react-router";

import CssBaseline from "@mui/material/CssBaseline";
import { I18nextProvider } from 'react-i18next';
import i18n from './context/language/i18n';

import { ColorModeContextProvider } from './context/colorMode';
import { MenuReplayProvider, MenuReplayLayout } from './context/MenuReplayContext';

import { isDrawerOpen, drawerMaxWidth, drawerMinWidth } from './components/menus/def';
import { Header } from './components/header';
import { Content } from './components/content';
import { Footer } from './components/footer';

import { Auth, SignIn, SignUp, ResetPassword, Terms, Language } from './components/auth';
import LoadingSpinner from './components/LoadingSpinner';
import { useSelector } from 'react-redux';
import { selectAuth } from './redux/infoSlice';

// Lazy load heavy components
const DashboardAdmin = lazy(() => import('./pages/dashboard/admin').then(module => ({ default: module.DashboardAdmin })));
const DashboardSupervisor = lazy(() => import('./pages/dashboard/supervisor').then(module => ({ default: module.DashboardSupervisor })));

const HospitalList = lazy(() => import('./pages/hospitals/list').then(module => ({ default: module.HospitalList })));
const HospitalAdd = lazy(() => import('./pages/hospitals/add').then(module => ({ default: module.HospitalAdd })));
const HospitalEdit = lazy(() => import('./pages/hospitals/edit').then(module => ({ default: module.HospitalEdit })));
const HospitalDetail = lazy(() => import('./pages/hospitals/detail').then(module => ({ default: module.HospitalDetail })));

const CaregiverList = lazy(() => import('./pages/caregivers/list').then(module => ({ default: module.CaregiverList })));
const CaregiverAdd = lazy(() => import('./pages/caregivers/add').then(module => ({ default: module.CaregiverAdd })));
const CaregiverEdit = lazy(() => import('./pages/caregivers/edit').then(module => ({ default: module.CaregiverEdit })));

const UserList = lazy(() => import('./pages/users/list').then(module => ({ default: module.UserList })));
const UserAdd = lazy(() => import('./pages/users/add').then(module => ({ default: module.UserAdd })));
const UserDetail = lazy(() => import('./pages/users/detail').then(module => ({ default: module.UserDetail })));
const UserIntakeAdd = lazy(() => import('./pages/users/intakeAdd').then(module => ({ default: module.UserIntakeAdd })));
const UserMedicationSettingAdd = lazy(() => import('./pages/users/medicationSettingAdd').then(module => ({ default: module.UserMedicationSettingAdd })));
const UserChangeDiaper = lazy(() => import('./pages/users/changeDiaper').then(module => ({ default: module.UserChangeDiaper })));
const UserCreateReport = lazy(() => import('./pages/users/createReport').then(module => ({ default: module.UserCreateReport })));
const UserMyInfo = lazy(() => import('./pages/users/myInfo').then(module => ({ default: module.UserMyInfo })));

const CommanderSupervisor = lazy(() => import('./pages/commanders/supervisor').then(module => ({ default: module.CommanderSupervisor })));
const CommanderAdmin = lazy(() => import('./pages/commanders/admin').then(module => ({ default: module.CommanderAdmin })));
const CommanderAdd = lazy(() => import('./pages/commanders/add').then(module => ({ default: module.CommanderAdd })));
const CommanderEdit = lazy(() => import('./pages/commanders/edit').then(module => ({ default: module.CommanderEdit })));
const CommanderEditSlot = lazy(() => import('./pages/commanders/editSlot').then(module => ({ default: module.CommanderEditSlot })));
const CommanderDetail = lazy(() => import('./pages/commanders/detail').then(module => ({ default: module.CommanderDetail })));

const SensorList = lazy(() => import('./pages/sensors/list').then(module => ({ default: module.SensorList })));
const SensorAdd = lazy(() => import('./pages/sensors/add').then(module => ({ default: module.SensorAdd })));
const SensorEdit = lazy(() => import('./pages/sensors/edit').then(module => ({ default: module.SensorEdit })));
const SensorDetail = lazy(() => import('./pages/sensors/detail').then(module => ({ default: module.SensorDetail })));

const SensorDataExport = lazy(() => import('./pages/sensorData/export').then(module => ({ default: module.SensorDataExport })));

const RelationshipList = lazy(() => import('./pages/relationships/list').then(module => ({ default: module.RelationshipList })));
const RelationshipAdd = lazy(() => import('./pages/relationships/add').then(module => ({ default: module.RelationshipAdd })));
const RelationshipManage = lazy(() => import('./pages/relationships/manage').then(module => ({ default: module.RelationshipManage })));

const NoticeAdmin = lazy(() => import('./pages/notices/admin').then(module => ({ default: module.NoticeAdmin })));
const NoticeSupervisor = lazy(() => import('./pages/notices/supervisor').then(module => ({ default: module.NoticeSupervisor })));
const NoticeList = lazy(() => import('./pages/notices/noticeList').then(module => ({ default: module.NoticeList })));
const NoticeDetail = lazy(() => import('./pages/notices/noticeDetail').then(module => ({ default: module.NoticeDetail })));
const NoticeNotification = lazy(() => import('./pages/notices/notificationList').then(module => ({ default: module.NoticeNotification })));

const IntakesAdmin = lazy(() => import('./pages/intakes/admin').then(module => ({ default: module.IntakesAdmin })));
const IntakesSetting = lazy(() => import('./pages/intakes/setting').then(module => ({ default: module.IntakesSetting })));

const SettingList = lazy(() => import('./pages/settings/list').then(module => ({ default: module.SettingList })));
const SettingNightMode = lazy(() => import('./pages/settings/nightMode').then(module => ({ default: module.SettingNightMode })));
const SettingAlarm = lazy(() => import('./pages/settings/alarm').then(module => ({ default: module.SettingAlarm })));

const Privacy = lazy(() => import('./pages/privacy').then(module => ({ default: module.Privacy })));
const PrivacyThirdParty = lazy(() => import('./pages/privacy/thirdParty').then(module => ({ default: module.PrivacyThirdParty })));
const PrivacyMarketing = lazy(() => import('./pages/privacy/marketing').then(module => ({ default: module.PrivacyMarketing })));


/*
appType
  - admin : 관리자 앱 (default)
  - commander : 중계 앱
  - user : 유저 앱
*/

function App() {
  const [params] = useSearchParams();
  const authInfo = useSelector(selectAuth);

  // 웹과 앱 구분
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isAndroid = userAgent.indexOf('android') > -1;
  const isIOS = /iphone|ipad|ipod/.test(userAgent);    

  const [appType, setAppType] = useState(params.get('appType') ? params.get('appType') : (isAndroid || isIOS ? 'user' : 'admin'));  

  const [menusWidth, setMenusWidth] = useState(isDrawerOpen ? drawerMaxWidth : drawerMinWidth);
  const [pageWidth, setPageWidth] = useState(window.innerWidth - (isDrawerOpen ? drawerMaxWidth : drawerMinWidth));

  useEffect(() => {
    let appType_ = params.get('appType') ? params.get('appType') : (localStorage.getItem('appType') || appType);
    localStorage.setItem('appType', appType_);
    setAppType(appType_);
  }, []);

  return (
    <div className="App">
    <I18nextProvider i18n={i18n}>
      <ColorModeContextProvider>
        <CssBaseline />

        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}          
          <Route path="/privacy" element={<Privacy/>}/>          
          <Route path="/privacy-third-party" element={<PrivacyThirdParty/>}/>
          <Route path="/privacy-marketing" element={<PrivacyMarketing/>}/>
          <Route path="/auth/*" element={
            <>
              <Routes>
                <Route path="signin" element={<SignIn/>}/>
                <Route path="signup" element={<SignUp/>}/>
                <Route path="reset-password" element={<ResetPassword/>}/>                
                <Route path="terms" element={<Terms/>}/>  
                <Route path="language" element={<Language/>}/>      
              </Routes>
            </>
          }/>

          {/* Protected Routes */}
          <Route path="/*" element={
            <Auth>
              {authInfo.isLoggedIn ? (
                <MenuReplayProvider>
                  <Header appType={appType} setMenusWidth={setMenusWidth} setPageWidth={setPageWidth}/>
                  <Content menusWidth={menusWidth} pageWidth={pageWidth}>
                    <Routes>
                      <Route element={<MenuReplayLayout />}>
                      <Route path="/" element={appType === 'commander' ? <CommanderDetail/> : JSON.parse(localStorage.getItem('meInfo'))?.grade === 1? <UserList/> 
                      : JSON.parse(localStorage.getItem('meInfo'))?.grade === 100 //&& sessionStorage.getItem('selectedHospitalId') === null 
                      ? <DashboardSupervisor/>
                      : <DashboardAdmin/>}/>
                      
                      <Route path="/dashboard">
                        <Route index element={
                          JSON.parse(localStorage.getItem('meInfo'))?.grade === 100//&& sessionStorage.getItem('selectedHospitalId') === null 
                          ? <DashboardSupervisor/> : 
                          <DashboardAdmin/>}/>                        
                        <Route path="admin" element={<DashboardAdmin/>}/>
                      </Route>  

                      <Route path="/hospitals">
                        <Route index element={<HospitalList/>}/>
                        <Route path="add" element={<HospitalAdd/>}/>
                        <Route path="edit" element={<HospitalEdit/>}/>
                        <Route path="detail" element={<HospitalDetail/>}/>
                      </Route>  
                      
                      <Route path="/caregivers">
                        <Route index element={<CaregiverList/>}/>
                        <Route path="add" element={<CaregiverAdd/>}/>
                        <Route path="edit" element={<CaregiverEdit/>}/>
                      </Route>  
                      
                      <Route path="/users">
                        <Route index element={<UserList/>}/>
                        <Route path="add" element={<UserAdd/>}/>
                        <Route path="detail" element={<UserDetail/>}/>
                        <Route path="intake-add" element={<UserIntakeAdd/>}/>
                        <Route path="medication-setting-add" element={<UserMedicationSettingAdd/>}/>                
                        <Route path="change-diaper" element={<UserChangeDiaper/>}/>
                        <Route path="create-report" element={<UserCreateReport/>}/>
                        <Route path="my-info" element={<UserMyInfo/>}/>
                        <Route path="reset-password" element={<ResetPassword/>}/>        
                      </Route>                

                      {(appType === 'commander') ? (
                        <Route path="/commanders">
                          <Route index element={<CommanderDetail/>}/>   
                          <Route path="add" element={<CommanderAdd/>}/>
                          <Route path="edit-slot" element={<CommanderEditSlot/>}/>
                        </Route>
                      ) : (
                        <Route path="/commanders">
                          <Route index element={JSON.parse(localStorage.getItem('meInfo'))?.grade === 100// && sessionStorage.getItem('selectedHospitalId') === null 
                          ? <CommanderSupervisor/> : <CommanderAdmin/>}/>                                          
                          <Route path="edit" element={<CommanderEdit/>}/>
                          <Route path="edit-slot" element={<CommanderEditSlot/>}/>
                          <Route path="detail" element={<CommanderDetail/>}/>                 
                        </Route>
                      )}

                      <Route path="/sensors">
                        <Route index element={<SensorList/>}/> 
                        <Route path="add" element={<SensorAdd/>}/>
                        <Route path="edit" element={<SensorEdit/>}/>
                        <Route path="detail" element={<SensorDetail/>}/>
                      </Route>

                      {appType === 'admin' && (
                      <Route path="/sensor-data/export" element={<SensorDataExport/>}/>
                      )}

                      {/* <Route path="/schedules" element={<Schedules/>}/>

                      <Route path="/records" element={<Records/>}/> */}

                      <Route path="/relationships">
                        <Route index element={appType === 'admin' ? <RelationshipManage/> : <RelationshipList/>}/>                  
                        <Route path="add" element={<RelationshipAdd/>}/>
                      </Route>

                      <Route path="/notices">                        
                        <Route index element={JSON.parse(localStorage.getItem('meInfo'))?.grade === 100// && sessionStorage.getItem('selectedHospitalId') === null 
                        ? <NoticeSupervisor/> : <NoticeAdmin/>}/>                                 
                        <Route path="notice-list" element={<NoticeList/>}/>
                        <Route path="notice-detail" element={<NoticeDetail/>}/>
                        <Route path="notification-list" element={<NoticeNotification/>}/>     
                      </Route>

                      <Route path="/intakes">
                        <Route index element={<IntakesAdmin/>}/>   
                        <Route path="admin" element={<IntakesAdmin/>}/>   
                        <Route path="setting" element={<IntakesSetting/>}/>
                      </Route>
                    
                      <Route path="/settings">
                        <Route index element={<SettingList/>}/>   
                        <Route path="night-mode" element={<SettingNightMode/>}/>                                 
                        <Route path="language" element={<Language/>}/>
                        <Route path="alarm" element={<SettingAlarm/>}/>                  
                      </Route>

                      <Route path="reset-password" element={<ResetPassword/>}/>        

                      </Route>
                    </Routes>
                  </Content>
                  <Outlet/>
                  <Footer/>
                </MenuReplayProvider>
              ) : (
                <Routes>
                  <Route path="*" element={<SignIn/>}/>
                </Routes>
              )}
            </Auth>
          }/>
        </Routes>
        </Suspense>

      </ColorModeContextProvider>
    </I18nextProvider>
    </div>
  );
}

export default App;
